import { CampaignDetails, EntityInfo, LocaleSettings, OrganizationContext } from '@mmt/events-schema'
import { Org, OrgValue } from '../../constants'

export function isBrowserEnv() {
  return typeof window !== 'undefined'
}

// Helper function to filter out null, undefined, and "NA" values
export function filterValidValues<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const result: Partial<T> = {}
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key]
      if (
        value !== null &&
        value !== undefined &&
        value !== 'NA' &&
        value !== '' &&
        (typeof value !== 'object' || Object.keys(value).length > 0) &&
        (!Array.isArray(value) || value.length > 0)
      ) {
        result[key] = value
      }
    }
  }
  return result
}

export type LoginInfo = {
  loginId: string
  loginType: string
  countryCode: string
  verified: boolean
  commId?: string
  status: string
}

interface UserCorpData {
  agencyUUID: string | null
  agentRole: string | null
  organizationId: string | null
  orgType: string | null
  employeeStatus: string | null
  employeeType: string | null
}

interface UserIdentities {
  mHash: string | null
  eHash: string | null
}
interface UserUid {
  uuid: string | null
  profileType: string | null
  phoneNumberHash: string | null
  emailHash: string | null
  nationality: string | null
  loginInfoList: LoginInfo[]
  userCorpData: UserCorpData
  identities: UserIdentities
  value: UserIdentities
}

export function readCookieValue(cookieString: string, cookieName: string): string | null {
  // Split the cookie string into individual cookies
  const cookies = cookieString.split('; ')

  // Loop through each cookie
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i]

    // If the cookie name matches, return the cookie value
    if (cookie.indexOf(cookieName + '=') === 0) {
      return cookie.substring(cookieName.length + 1)
    }
  }

  // If the cookie was not found, return null
  return null
}

function xorDecrypt(ciphertext: string, key: string): string | null {
  try {
    let plaintext = ''
    let keyIndex = 0
    if (!key) throw new Error('Key is empty')
    for (let i = 0; i < ciphertext.length; i += 1) {
      const charCode = ciphertext.charCodeAt(i)
      const keyChar = key.charCodeAt(keyIndex)
      // eslint-disable-next-line no-bitwise
      const decryptedCharCode = charCode ^ keyChar
      plaintext += String.fromCharCode(decryptedCharCode)
      keyIndex = (keyIndex + 1) % key.length
    }
    return plaintext
  } catch (err) {
    console.error('An error occurred during the decryption process:', err)
    return null
  }
}

export function getUserUid(isNotEncrypted?: boolean): UserUid | null {
  const itemVal: string | null = sessionStorage.getItem('userInfo') || localStorage.getItem('userInfo')
  if (!itemVal) {
    console.log('userInfo is not found in sessionStorage')
    return null
  }
  if (isNotEncrypted) {
    return JSON.parse(itemVal)
  }
  const prefixRegex = new RegExp(`^enct-`)
  const cleanedItemVal = itemVal.replace(prefixRegex, '')
  const decryptedVal = xorDecrypt(cleanedItemVal, 'awefewfuihewfhewfuhiebc')
  if (!decryptedVal) {
    console.error('Decryption failed')
    return null
  }
  try {
    const encryptedVal = JSON.parse(decryptedVal)
    return encryptedVal // Assuming encryptedVal is a string. If not, you may need to adjust this.
  } catch (e) {
    console.error('Error parsing JSON:', e)
    return null
  }
}

export function getMarketingCloudVisitorID(): string {
  const localStorageKey = 'marketingCloudVisitorID'
  const windowVisitorID = window?.s?.marketingCloudVisitorID
  const localStorageVisitorID = localStorage.getItem(localStorageKey)

  const cookieValue = readCookieValue(document?.cookie, 's_ecid')
  if (cookieValue) {
    const decodedCookieValue = decodeURI(cookieValue)
    return decodedCookieValue?.split('|')[1]
  }

  if (windowVisitorID) {
    if (windowVisitorID != localStorageVisitorID) {
      localStorage.setItem(localStorageKey, windowVisitorID)
    }
    return windowVisitorID
  }

  if (localStorageVisitorID) {
    return localStorageVisitorID
  }

  return 'NA'
}

export function getUserFromStorage(org?: string): UserUid | null {
  return getUserUid(org === OrgValue.GI)
}

type UserContext = {
  uuid?: string | null
  profile_type?: string | null
}

// Utility method to return userUUIDPt
export function getUserUUIDPt(userContext?: UserContext): unknown {
  if (!userContext) {
    return null
  }
  const { uuid = '', profile_type = 'PERSONAL' } = userContext

  if (profile_type && uuid) {
    return `${uuid}/${profile_type.toUpperCase()}`
  }
  return null
}

export function getRequestQueryPrem(key?: string): string | undefined {
  const queryString = window.location.search.substring(1)
  const queryParams = queryString.split('&')

  for (let i = 0; i < queryParams.length; i++) {
    const pair = queryParams[i].split('=')
    const paramName = decodeURIComponent(pair[0])
    const paramValue = decodeURIComponent(pair[1])
    if (paramName === key) {
      return paramValue // Return the value if found
    }
  }
}

export function getCampaignDetails(): CampaignDetails | null {
  const getCMPName = getRequestQueryPrem('cmp')
  if (!getCMPName) {
    return null
  }
  return {
    campaign_key: getCMPName,
  }
}

export function updateDomain(url?: string): string {
  if (url) {
    if (!/^https?:\/\//i.test(url)) {
      return `https://${url}`
    }
    return url
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  const baseDomain = hostname?.split('.')?.slice(-2)?.join('.') || 'makemytrip.com'

  return `https://pdt.${baseDomain}`
}

type ReqHeaders = Record<string, string>
export function updateHeaders(headers: ReqHeaders, url: string): ReqHeaders {
  const currentDomain = url || (typeof window !== 'undefined' ? window.location.hostname : '')
  let additionalHeader = {}
  if (isMakeMyTripDomain(currentDomain)) {
    additionalHeader = {
      'entity-name': currentDomain && currentDomain.includes('.global') ? 'global' : 'india',
    }
  }
  return {
    ...headers,
    ...additionalHeader,
  }
}

const CURRENCY_MAP = {
  in: 'inr',
  ae: 'aed',
  sa: 'sar',
}

export function isGoibiboDomain(): boolean {
  const currentDomain = typeof window !== 'undefined' ? window.location.hostname : ''
  return currentDomain.includes('goibibo')
}

export function isMakeMyTripDomain(domain?: string): boolean {
  const currentDomain = domain || (typeof window !== 'undefined' ? window.location.hostname : '')
  return currentDomain.includes('makemytrip')
}

export function getEntityInfoForGI(): EntityInfo {
  return {
    entity: 'india',
    currency: 'inr',
    region: 'in',
    user_language: 'eng',
    organisation: 'gi',
  }
}

export function getEntityInfoForMMT(): EntityInfo {
  const currentDomain = typeof window !== 'undefined' ? window.location.hostname : ''
  const entity = currentDomain && currentDomain.includes('.global') ? 'global' : 'india'
  const localeSettings = getLocaleSettings()
  const { region, userCountry, language, userCurrency, currency, isWorldwide } = localeSettings

  return {
    entity,
    currency,
    region,
    user_currency: userCurrency,
    user_country: isWorldwide ? userCountry : region,
    user_language: language,
    organisation: 'mmt',
  }
}

export function getEntityInfo(): EntityInfo {
  if (isMakeMyTripDomain()) {
    return getEntityInfoForMMT()
  }
  if (isGoibiboDomain()) {
    return getEntityInfoForGI()
  }
  // Default to MMT implementation for backward compatibility
  return getEntityInfoForMMT()
}

export function getLocaleSettings(): LocaleSettings {
  const userCountry = readCookieValue(document?.cookie, 'cc')?.toLowerCase() || 'in'
  const region = readCookieValue(document?.cookie, 'ccde')?.toLowerCase() || 'in'
  const isWW = readCookieValue(document?.cookie, 'isWorldwide')
  const language = readCookieValue(document?.cookie, 'lang')?.toLowerCase() || 'eng'
  const userCurrency = readCookieValue(document?.cookie, 'userCurrency')?.toLowerCase() || ''
  const isWorldwide = isWW ? parseInt(isWW) : 0
  const currency = region ? CURRENCY_MAP[region as keyof typeof CURRENCY_MAP] : ''

  return {
    currency,
    isWorldwide,
    language,
    region,
    userCountry,
    userCurrency,
  }
}

export const PROFILE_TYPE_MAP = {
  b2b: 'business',
  b2c: 'personal',
  myPartner: 'cta',
}

export function getProfileType(): string {
  const myBusinessSubscription = readCookieValue(document?.cookie, 'myBusinessSubscription')
  return PROFILE_TYPE_MAP[myBusinessSubscription as keyof typeof PROFILE_TYPE_MAP] || PROFILE_TYPE_MAP.b2c
}

export function isGI(org: Org): boolean {
  return org === OrgValue.GI
}

export function parsePipeDelimitedString(str: string): Record<string, string> {
  if (!str || typeof str !== 'string') {
    return {}
  }

  const result: Record<string, string> = {}
  const pairs = str.split('|')

  pairs.forEach(pair => {
    const [key, value] = pair.split(':')
    if (key && value !== undefined) {
      result[key.trim()] = value.trim()
    }
  })

  return result
}

export function getOrganizationContext(): OrganizationContext {
  const profileType = getProfileType()
  const mmtUserDet = getUserFromStorage()
  if (profileType === PROFILE_TYPE_MAP.myPartner) {
    return {
      user_role: mmtUserDet?.userCorpData?.agentRole,
      org_id: mmtUserDet?.userCorpData?.agencyUUID,
    }
  }

  if (profileType === PROFILE_TYPE_MAP.b2b) {
    const orgContext = localStorage.getItem('myBizOrgContext') || ''
    if (!orgContext) {
      return {}
    }
    const parsedOrgContext = parsePipeDelimitedString(orgContext)
    return {
      org_id: mmtUserDet?.userCorpData?.organizationId,
      org_type: mmtUserDet?.userCorpData?.orgType,
      verification_status: mmtUserDet?.userCorpData?.employeeStatus,
      user_role: mmtUserDet?.userCorpData?.employeeType,
      ...parsedOrgContext,
    }
  }

  return {}
}
